import { Component, ElementRef, type OnDestroy, type OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { type FormArray, FormBuilder, type FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { combineLatest, type Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { GtmService } from '@fcom/common/gtm';
import { LoaderType, type SelectOption } from '@fcom/common/interfaces';
import { ScrollService } from '@fcom/common/services';
import { type AppState } from '@fcom/core';
import { type Profile } from '@fcom/core-api/login';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { EMAIL_REGEX, enumValues, LocalDate, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import {
  bicValidator,
  dateValidator,
  disableConditionalFields,
  enableConditionalFields,
  FormStatus,
  frequentFlyerValidator,
  getCountryOptions,
  ibanValidator,
  pnrFormatValidator,
  softTicketNumberValidator,
  trackFormState,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import {
  ButtonType,
  NotificationLayout,
  NotificationTheme,
  type RadioItemGroupOption,
  TEXTAREA_LIMIT_3000,
  TextInputComponent,
  TextInputType,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import {
  EnabledOnlineBankFields,
  type RefundForm,
  RefundFormCountries,
  RefundFormOnlineBankFields,
  type RefundFormPassengers,
  type RefundFormPassengersTravelExtras,
  RefundFormPurchaseChannels,
  RefundFormRefundReasons,
  RefundPaymentMethods,
  RefundPaymentTypes,
  RefundTypes,
} from '../interfaces/refund-form.interface';
import { SubmitService } from '../services/submit.service';
import { PAYMENT_IFSC_BIK_PATTERN, PAYMENT_INN_PATTERN, PAYMENT_KPP_PATTERN, PHONE_NUMBER_PATTERN } from '../utils';

const REFUND_FORM = 'refund-form';

@Component({
  selector: 'fin-refund-form',
  templateUrl: 'refund-form.component.html',
  styleUrls: ['./refund-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RefundFormComponent implements OnInit, OnDestroy {
  protected readonly DatepickerTheme = DatepickerTheme;

  readonly ButtonType = ButtonType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly RefundFormPurchaseChannels = RefundFormPurchaseChannels;
  readonly RefundFormRefundReasons = RefundFormRefundReasons;
  readonly RefundPaymentMethods = RefundPaymentMethods;
  readonly RefundPaymentTypes = RefundPaymentTypes;
  readonly RefundTypes = RefundTypes;
  readonly TextInputType = TextInputType;
  readonly additionalInformationCharacterLimit = TEXTAREA_LIMIT_3000;

  @ViewChild('additionalInformation') additionalInformation: TextInputComponent;

  @ViewChild('formContainer', { static: true })
  formContainer: ElementRef;

  minDateFlight: LocalDate;
  maxDateFlight: LocalDate;
  minDateBooking: LocalDate;
  maxDateBooking: LocalDate;

  profile: Profile;
  reactiveForm: FormGroup<RefundForm>;

  formStatus: FormStatus = FormStatus.INITIAL;
  countries: SelectOption[];
  travelExtraTypeOptions$: Observable<SelectOption[]>;

  refundReasonsFlight: RadioItemGroupOption[] = [
    { value: RefundFormRefundReasons.FLIGHT_CANCELLED_BY_FINNAIR },
    { value: RefundFormRefundReasons.CANCELLED_BY_TICKET_RULES },
    { value: RefundFormRefundReasons.CANCELLED_WITHIN_24_HOURS },
  ];

  paymentMethods = [RefundPaymentMethods.MONEY_BACK, RefundPaymentMethods.GIFT_VOUCHER];

  refundReasonsExtras: RadioItemGroupOption[] = [
    { value: RefundFormRefundReasons.FLIGHT_CANCELLED_BY_FINNAIR },
    { value: RefundFormRefundReasons.EXTRA_NOT_DELIVERED },
    { value: RefundFormRefundReasons.CHANGED_FLIGHT_OR_SEAT },
    { value: RefundFormRefundReasons.CANCELLED_WITHIN_24_HOURS },
  ];

  availableRefundTypes: RadioItemGroupOption[] = [
    { value: RefundTypes.FLIGHT },
    { value: RefundTypes.EXTRAS },
    { value: RefundTypes.FLIGHT_AND_EXTRAS },
  ];

  requestContainsExtras = false;
  requestContainsFlight = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private submitService: SubmitService,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private store$: Store<AppState>
  ) {}

  get characterCount(): number {
    return this.additionalInformation?.control?.value?.length || 0;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        getCountryOptions(this.languageService),
        this.languageService.translate('SERVICE_FORMS.serviceForms.refundForm.otherCountry'),
      ]).subscribe(([allCountries, countryName]) => {
        const countryValues = enumValues(RefundFormCountries);

        this.countries = [
          ...allCountries
            /** @todo Enable bank details for Russia. */
            .filter((country) => country.value !== RefundFormCountries.RUSSIA)
            .filter((country) => countryValues.includes(country.value as RefundFormCountries))
            .sort((a, b) => a.name.localeCompare(b.name)),
          { value: RefundFormCountries.OTHER_COUNTRIES, name: countryName },
        ];
      })
    );
    this.createForm();
    this.initFormSubscriptions();
    this.translateTravelExtraTypeOptions();
    this.minDateFlight = LocalDate.forDate(new Date().getFullYear() - 1, 1, 1);
    this.maxDateFlight = LocalDate.forDate(new Date().getFullYear() + 1, 12, 31);
    this.minDateBooking = LocalDate.forDate(new Date().getFullYear() - 1, 1, 1);
    this.maxDateBooking = LocalDate.now();
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(REFUND_FORM, 'send-attempt', this.gtmService);
    this.subscriptions.add(
      this.submitService
        .requestRefund(this.reactiveForm.value)
        .pipe(take(1))
        .subscribe({
          next: () => {
            trackFormState(REFUND_FORM, 'success', this.gtmService);
            this.formStatus = FormStatus.COMPLETE;
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(REFUND_FORM, 'error', this.gtmService);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  get passengerFormGroupConfig(): RefundFormPassengers {
    return {
      firstname: this.fb.control('', [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)]),
      lastname: this.fb.control('', [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)]),
      finnairPlusNumber: this.fb.control('', [frequentFlyerValidator()]),
      ticketNumber: this.fb.control(
        '',
        this.requestContainsFlight ? [Validators.required, softTicketNumberValidator()] : []
      ),
      travelExtras: this.fb.array<FormGroup<RefundFormPassengersTravelExtras>>([]),
      preorderBagNumber: this.fb.control('', []),
    };
  }

  get travelExtrasFormGroupConfig(): RefundFormPassengersTravelExtras {
    return {
      emdNumber: this.fb.control('', [Validators.required, softTicketNumberValidator()]),
      refundItemType: this.fb.control('', Validators.required),
    };
  }

  get selectedPurchaseChannel(): RefundFormPurchaseChannels {
    return this.reactiveForm.controls.purchaseChannel.value ? this.reactiveForm.controls.purchaseChannel.value : null;
  }

  get selectedRefundType(): RefundTypes {
    return this.reactiveForm.controls.refundType.value ? this.reactiveForm.controls.refundType.value : null;
  }

  get selectedRefundReason(): RefundFormRefundReasons {
    return this.reactiveForm.controls.refundReason.value ? this.reactiveForm.controls.refundReason.value : null;
  }

  get selectedPaymentMethod(): RefundPaymentMethods {
    return this.reactiveForm.controls.paymentMethod.value ? this.reactiveForm.controls.paymentMethod.value : null;
  }

  get selectedPaymentType(): RefundPaymentTypes {
    return this.reactiveForm.controls.paymentType.value ? this.reactiveForm.controls.paymentType.value : null;
  }

  get passengersData(): FormArray {
    return this.reactiveForm.controls.passengers as FormArray;
  }

  createForm(): void {
    this.reactiveForm = this.fb.group({
      purchaseChannel: this.fb.control('' as RefundFormPurchaseChannels, Validators.required),
      refundType: this.fb.control('' as RefundTypes, Validators.required),
      refundReason: this.fb.control('' as RefundFormRefundReasons, Validators.required),
      reservationNumber: this.fb.control('', [Validators.required, pnrFormatValidator()]),
      flightNumber: this.fb.control('', Validators.required),
      flightDate: this.fb.control('', [Validators.required, dateValidator()]),
      bookingDate: this.fb.control('', [Validators.required, dateValidator()]),
      cancellationDate: this.fb.control('', [Validators.required, dateValidator()]),
      phoneNumber: this.fb.control('', [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]), // International format, plus followed by max length string
      email: this.fb.control('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
      passengers: this.fb.array<FormGroup<RefundFormPassengers>>([]),
      paymentMethod: this.fb.control('' as RefundPaymentMethods, Validators.required),
      paymentType: this.fb.control('' as RefundPaymentTypes, Validators.required),
      iban: this.fb.control('', [Validators.required, ibanValidator()]),
      bic: this.fb.control('', [Validators.required, bicValidator()]),
      accountHolder: this.fb.control('', [Validators.required, Validators.pattern(NAME_PATTERN)]),
      accountHolderAddress: this.fb.control('', Validators.required),
      bankName: this.fb.control('', Validators.required),
      bankAddress: this.fb.control('', Validators.required),
      additionalInformation: this.fb.control('', Validators.maxLength(this.additionalInformationCharacterLimit)),
      country: [this.languageService.ddsLocaleValue.countrySite, [Validators.required]],
      accountNumber: this.fb.control('', Validators.required),
      ncc: this.fb.control('', Validators.required),
      ifsc: this.fb.control('', [Validators.required, Validators.pattern(PAYMENT_IFSC_BIK_PATTERN)]),
      bik: this.fb.control('', [Validators.required, Validators.pattern(PAYMENT_IFSC_BIK_PATTERN)]),
      correspondentBankData: this.fb.control('', Validators.required),
      kpp: this.fb.control('', [Validators.required, Validators.pattern(PAYMENT_KPP_PATTERN)]),
      inn: this.fb.control('', [Validators.required, Validators.pattern(PAYMENT_INN_PATTERN)]),
      patronymicName: this.fb.control('', Validators.required),
    });
  }

  initFormSubscriptions(): void {
    this.subscriptions.add(
      this.reactiveForm.controls.passengers.valueChanges
        .pipe(filter((passengers) => passengers.length && this.profile && !passengers.at(0).firstname))
        .subscribe(() => {
          this.prefillContactDetails();
          this.prefillPassengerDetails();
        })
    );
    this.subscriptions.add(
      this.reactiveForm.controls.purchaseChannel.valueChanges.subscribe(() =>
        this.resetInputs(['purchaseChannel', 'country'])
      )
    );
    this.subscriptions.add(
      this.reactiveForm.controls.refundType.valueChanges.subscribe(() =>
        this.resetInputs(['purchaseChannel', 'refundType', 'country'])
      )
    );
    this.subscriptions.add(
      this.reactiveForm.controls.refundReason.valueChanges.subscribe(() => {
        this.updateBookingDetailValidators();
        this.resetPaymentMethod();
      })
    );
    this.subscriptions.add(
      this.reactiveForm.controls.paymentType.valueChanges.subscribe(() => this.resetBankDetails())
    );
    this.subscriptions.add(
      this.reactiveForm.controls.paymentMethod.valueChanges.subscribe(() => {
        this.updatePaymentTypeValidators();
        this.reactiveForm.controls.paymentType.reset();
      })
    );
    this.subscriptions.add(
      this.reactiveForm.controls.country.valueChanges.subscribe(() => this.enableBankFieldsForCountry())
    );
    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status) => status === 'INVALID'),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe(() => {
          trackFormState(REFUND_FORM, 'start', this.gtmService);
        })
    );
    this.subscriptions.add(
      this.store$
        .pipe(profileOrUndefinedIfNotLoggedIn(), distinctUntilChanged())
        .pipe(filter((profile) => Boolean(profile)))
        .subscribe((profile) => {
          this.profile = profile;
          this.prefillContactDetails();
          this.prefillPassengerDetails();
        })
    );
  }

  prefillContactDetails(): void {
    const email = this.profile?.email ?? '';
    const countryCode = this.profile?.parsedMobilePhone?.countryCode ?? '';
    const nationalNumber = this.profile?.parsedMobilePhone?.nationalNumber ?? '';
    const phoneNumber = countryCode && nationalNumber ? `+${countryCode}${nationalNumber}` : '';
    if (email && !this.reactiveForm.get('email').value) {
      this.reactiveForm.get('email').setValue(email);
      this.reactiveForm.get('phoneNumber').setValue(phoneNumber);
    }
  }

  prefillPassengerDetails(): void {
    const firstPassenger = this.reactiveForm.controls.passengers.at(0);
    if (firstPassenger && !firstPassenger.get('firstname').value) {
      firstPassenger.get('firstname').setValue(this.profile.firstname);
      firstPassenger.get('lastname').setValue(this.profile.lastname);
      firstPassenger.get('finnairPlusNumber').setValue(this.profile.memberNumber);
    }
  }

  resetPassengers(): void {
    while (0 !== this.passengersData.length) {
      this.passengersData.removeAt(0);
    }
  }

  translateTravelExtraTypeOptions(): void {
    this.travelExtraTypeOptions$ = this.languageService
      .translate('SERVICE_FORMS.serviceForms.refundForm.travelExtraType')
      .pipe(
        map((options) =>
          Object.keys(options).map(
            (value) =>
              ({
                value: value,
                name: options[value],
              }) as SelectOption
          )
        )
      );
  }

  updateBookingDetailValidators(): void {
    this.reactiveForm.controls.flightNumber.setValidators(null);
    if (this.selectedRefundReason === RefundFormRefundReasons.CANCELLED_WITHIN_24_HOURS) {
      this.reactiveForm.controls.bookingDate.setValidators([Validators.required, dateValidator()]);
      this.reactiveForm.controls.bookingDate.updateValueAndValidity();
      this.reactiveForm.controls.cancellationDate.setValidators([Validators.required, dateValidator()]);
      this.reactiveForm.controls.flightDate.setValidators(null);
    } else {
      this.reactiveForm.controls.bookingDate.setValidators(null);
      this.reactiveForm.controls.cancellationDate.setValidators(null);
      this.reactiveForm.controls.flightDate.setValidators([Validators.required, dateValidator()]);
      this.reactiveForm.controls.flightDate.updateValueAndValidity();
    }
  }

  updatePaymentTypeValidators(): void {
    if (this.selectedPaymentMethod !== RefundPaymentMethods.MONEY_BACK) {
      this.reactiveForm.controls.paymentType.setValidators(null);
    } else {
      this.reactiveForm.controls.paymentType.setValidators([Validators.required]);
    }
  }

  resetPaymentMethod(): void {
    this.reactiveForm.controls.paymentMethod.setValue(RefundPaymentMethods.MONEY_BACK);
  }

  updateBankDetailValidators(): void {
    if (this.selectedPaymentType === RefundPaymentTypes.ONLINE_BANK) {
      this.reactiveForm.controls.country.enable();
      this.enableBankFieldsForCountry();
    } else {
      const bankingFields = Object.values(RefundFormOnlineBankFields).filter(
        (field) => field !== RefundFormOnlineBankFields.BANK_COUNTRY
      );
      disableConditionalFields(bankingFields, this.reactiveForm);
    }
  }

  resetInputs(fieldsToSkip: string[]): void {
    for (const field in this.reactiveForm.controls) {
      if (!fieldsToSkip.includes(field)) {
        this.reactiveForm.get(field).reset();
      }
    }
    this.requestContainsExtras = [RefundTypes.EXTRAS, RefundTypes.FLIGHT_AND_EXTRAS].includes(this.selectedRefundType);
    this.requestContainsFlight = [RefundTypes.FLIGHT, RefundTypes.FLIGHT_AND_EXTRAS].includes(this.selectedRefundType);
    this.resetPassengers();
  }

  resetBankDetails(): void {
    Object.values(RefundFormOnlineBankFields).forEach((input): void => {
      if (input !== 'country') {
        this.reactiveForm.get(input).reset();
      }
    });

    this.updateBankDetailValidators();
  }

  stripSpaces(fieldRef: string): void {
    const formField = this.reactiveForm.get(fieldRef);
    formField.setValue(formField.value.replace(/\s/g, ''));
  }

  enableBankFieldsForCountry(): void {
    const enabledFields = [];
    const disabledFields = [];
    const country = this.reactiveForm.controls.country.value;

    EnabledOnlineBankFields.forEach((bankField) => {
      if (bankField.enabledCountries.includes(country) || bankField.enabledCountries.includes('ALL')) {
        enabledFields.push(bankField.name);
      } else {
        disabledFields.push(bankField.name);
      }
    });

    enableConditionalFields(enabledFields, this.reactiveForm);
    disableConditionalFields(disabledFields, this.reactiveForm);
  }
}
